import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppLogo } from "@/components/AppLogo";
import { useState } from "react";
import { safeAuth } from "@/lib/auth-errors";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ServiceActivationWizard } from "@/components/ServiceActivationWizard";
import { SERVICES, SERVICE_LABEL, type ServiceType } from "@/lib/services";

export const Route = createFileRoute("/partner-register")({
  head: () => ({
    meta: [
      { title: "Become a partner · Landoo" },
      {
        name: "description",
        content:
          "Register as a Landoo partner. Choose one primary service — Sale, Rent, Lease or Parking — and complete its verification.",
      },
      { property: "og:title", content: "Become a partner · Landoo" },
      {
        property: "og:description",
        content: "Register as a Landoo partner with one primary service.",
      },
    ],
  }),
  component: PartnerRegister,
});

const inp =
  "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30";

type Phase = "account" | "service" | "activation";

function PartnerRegister() {
  const nav = useNavigate();
  const [phase, setPhase] = useState<Phase>("account");

  // Step 1 — account creation
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [creating, setCreating] = useState(false);

  // Step 2 — primary service
  const [service, setService] = useState<ServiceType | null>(null);

  const sendOtp = async () => {
    if (!/^\+\d{8,15}$/.test(mobile))
      return toast.error("Enter your mobile in international format, e.g. +919876543210");
    const { error } = await safeAuth(() => supabase.auth.signInWithOtp({ phone: mobile }));
    setOtpSent(true);
    if (error) {
      toast.warning(
        "SMS delivery is not configured yet — mobile verification stays pending for admin review.",
      );
      return;
    }
    toast.success("OTP sent to your mobile.");
  };

  const verifyOtp = async () => {
    if (otp.length < 4) return toast.error("Enter the code you received.");
    const { error } = await safeAuth(() =>
      supabase.auth.verifyOtp({ phone: mobile, token: otp, type: "sms" }),
    );
    if (error) return toast.error(error);
    setMobileVerified(true);
    toast.success("Mobile verified.");
  };

  const validateAccount = () => {
    if (fullName.trim().length < 3) return "Enter your full name.";
    if (!/^\+?\d{8,15}$/.test(mobile)) return "Enter a valid mobile number.";
    if (!/^\S+@\S+\.\S+$/.test(email)) return "Enter a valid email address.";
    if (password.length < 6) return "Password must be at least 6 characters.";
    if (password !== confirm) return "Passwords do not match.";
    return null;
  };

  const createAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validateAccount();
    if (invalid) return toast.error(invalid);
    if (!service) return toast.error("Select your primary service.");

    setCreating(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName,
          role: "partner",
          phone: mobile,
          mobile_verified: mobileVerified,
        },
      },
    });

    if (error) {
      setCreating(false);
      return toast.error(error.message);
    }

    if (data.user) {
      await supabase
        .from("profiles")
        .update({ full_name: fullName, phone: mobile })
        .eq("id", data.user.id);
      await supabase
        .from("user_roles")
        .upsert({ user_id: data.user.id, role: "partner" }, { onConflict: "user_id,role" });
      // Exactly one primary service — no other service access is granted.
      await supabase.from("partner_service_profiles").insert({
        partner_id: data.user.id,
        service_type: service,
        is_primary: true,
        registration_data: { phone: mobile, mobile_verified: mobileVerified } as never,
        service_status: "registration_draft",
      });
    }
    setCreating(false);

    if (!data.session) {
      toast.success(
        "Check your email to confirm your account, then finish your service activation.",
      );
      nav({ to: "/login" });
      return;
    }
    setPhase("activation");
  };

  if (phase === "activation" && service) {
    return (
      <div className="min-h-screen bg-cream/40 p-4">
        <div className="mx-auto w-full max-w-3xl">
          <div className="mb-4">
            <AppLogo />
          </div>
          <h1 className="text-xl font-bold text-brown-dark">
            {SERVICE_LABEL[service]} service activation
          </h1>
          <p className="mb-4 mt-1 text-sm text-muted-foreground">
            Complete partner type, business information, verification, agreement and subscription
            for your {SERVICE_LABEL[service]} service.
          </p>
          <ServiceActivationWizard
            serviceType={service}
            onDone={() =>
              nav({
                to: "/partner/services/$serviceType",
                params: { serviceType: service },
                replace: true,
              })
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream/40 p-4">
      <div className="w-full max-w-2xl rounded-3xl border border-border bg-card p-5 shadow-float sm:p-6">
        <div className="mb-4">
          <AppLogo />
        </div>
        <h1 className="text-xl font-bold text-brown-dark">Partner registration</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Step {phase === "account" ? "1" : "2"} of 9 · A partner account cannot publish listings
          until one service is verified, agreed, paid and approved.
        </p>

        {phase === "account" && (
          <form
            className="mt-5 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              const v = validateAccount();
              if (v) return toast.error(v);
              setPhase("service");
            }}
          >
            <input
              className={inp}
              required
              placeholder="Full name *"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />

            <div className="rounded-2xl border border-border bg-background p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Mobile verification
              </div>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  className={inp}
                  required
                  placeholder="Mobile number (+91…) *"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                />
                <button
                  type="button"
                  onClick={sendOtp}
                  className="shrink-0 rounded-xl border border-border px-4 py-2.5 text-xs font-semibold text-brown-dark hover:bg-brand-soft"
                >
                  {otpSent ? "Resend OTP" : "Send OTP"}
                </button>
              </div>
              {otpSent && !mobileVerified && (
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    className={inp}
                    inputMode="numeric"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={verifyOtp}
                    className="shrink-0 rounded-xl bg-brown px-4 py-2.5 text-xs font-semibold text-primary-foreground"
                  >
                    Verify
                  </button>
                </div>
              )}
              <p className="mt-2 text-[11px] text-muted-foreground">
                {mobileVerified
                  ? "Mobile verified."
                  : "If SMS is unavailable, mobile verification is completed by admin review."}
              </p>
            </div>

            <input
              className={inp}
              type="email"
              required
              placeholder="Email address *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                className={inp}
                type="password"
                required
                minLength={6}
                placeholder="Password *"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <input
                className={inp}
                type="password"
                required
                minLength={6}
                placeholder="Confirm password *"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>

            <button className="w-full rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft transition hover:brightness-105">
              Continue to service selection
            </button>
          </form>
        )}

        {phase === "service" && (
          <form className="mt-5" onSubmit={createAccount}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Choose exactly one primary service
            </div>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              {SERVICES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setService(s.value)}
                  className={`rounded-2xl border p-5 text-left transition ${
                    service === s.value
                      ? s.value === "parking"
                        ? "border-parking bg-parking/10"
                        : "border-brown bg-brand-soft"
                      : "border-border bg-background hover:bg-secondary/60"
                  }`}
                >
                  <div className="text-base font-bold text-brown-dark">{s.cardTitle}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{s.blurb}</div>
                </button>
              ))}
            </div>

            <div className="mt-4 rounded-xl border border-dashed border-border bg-cream/50 p-3 text-[11px] text-muted-foreground">
              Additional services can be activated later as add-ons — each with its own
              registration, verification, agreement, subscription and approval.
            </div>

            <label className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
              <input type="checkbox" required className="mt-0.5" />I agree to Landoo's{" "}
              <Link to="/legal-info" className="underline">
                Terms
              </Link>
              .
            </label>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setPhase("account")}
                className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-brown-dark"
              >
                Back
              </button>
              <button
                disabled={creating || !service}
                className="flex-1 rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft transition hover:brightness-105 disabled:opacity-50"
              >
                {creating
                  ? "Creating partner account…"
                  : service
                    ? `Continue with ${SERVICE_LABEL[service]}`
                    : "Select a service"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-4 text-xs text-muted-foreground">
          Looking for a property?{" "}
          <Link to="/register" className="font-semibold text-brown-dark hover:underline">
            Register as a customer
          </Link>
        </div>
      </div>
    </div>
  );
}
